import { MonthNumber } from './types';
import range from 'lodash/range';
import { t } from 'i18next';

export function generateExerciceList() {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => currentYear - 2 + i);
}

/**
 * Return the current month, in js 0-index format (january is 0), for this exercice.
 *
 * If the exercice is finished, it is the current month,
 * otherwise it is the previous month, since the current month would
 * not have anything, except for january where the current month is displayed.
 *
 * @param exercice - The exercice year.
 */
export const getCurrentMonthForExercice = (exercice: number): MonthNumber => {
    const now = new Date();
    if (now.getFullYear() > exercice || now.getMonth() == 0) {
        return (now.getMonth() + 1) as MonthNumber;
    }
    return now.getMonth() as MonthNumber;
};

/**
 * Return all the valid month for a given exercice.
 *
 * A valid month is a month where there can be data, so all month if it is a past
 * exercice, or up until the previous month for current exercice.
 *
 * @param exercice - The exercice year.
 */
export const getExerciceMonths = (exercice: number): MonthNumber[] => {
    const now = new Date();
    if (now.getFullYear() > exercice) {
        return range(1, 13) as MonthNumber[];
    }
    return range(1, getCurrentMonthForExercice(exercice) + 1) as MonthNumber[];
};

/**
 * Return the localized month, using the user locale.
 *
 * @param month - The month number, as 1-indexed (january is 1).
 */
export const getLocalizedMonth = (month: MonthNumber): string => {
    const dummyDate = new Date(2000, month - 1, 1);
    return dummyDate.toLocaleString(navigator.language, { month: 'long' });
};

type MonthFormat = 'long' | 'short';

/**
 * Return the monthly period bound for the exercice.
 *
 * @param exercice - The exercise year for which the monthly period needs to be calculated.
 * @returns An object with `startMonth` as the period first month, and `endMonth` as its last.
 */
export const getExerciceMonthlyPeriod = (
    exercice: number,
    format: MonthFormat = 'long',
): { startMonth: string; endMonth: string } => {
    const now = new Date();

    let endMonth = 11;
    if (now.getFullYear() <= exercice) {
        endMonth = now.getMonth() > 0 ? now.getMonth() - 1 : 0;
    }

    const startDate = new Date(2000, 0, 1);
    const endDate = new Date(2000, endMonth, 1);
    const startLocalized = startDate.toLocaleString(navigator.language, {
        month: format,
    });
    const endLocalized = endDate.toLocaleString(navigator.language, {
        month: format,
    });

    return {
        startMonth: startLocalized,
        endMonth: endLocalized,
    };
};

/**
 * Generates a string representing the localized monthly period of an exercise.
 *
 * @param exercice - The exercise year for which the monthly period needs to be calculated.
 * @returns A string representing the monthly period in the format "(Start Month - End Month)".
 *
 * @example
 * // Assuming the current date is 2024-07-01.
 * getExerciceMonthlyPeriod(2024); // Returns "(January - June)" (if the local is english)
 * getExerciceMonthlyPeriod(2023); // Returns "(January - December)"
 *
 * @throws Will throw an error if the input `exercice` is not a valid number.
 */
export const getExerciceMonthlyPeriodFormatted = (exercice: number): string => {
    const { startMonth, endMonth } = getExerciceMonthlyPeriod(exercice);
    return `(${startMonth} - ${endMonth})`;
};

export const getExercicePeriodFormatted = (exercice: number): string => {
    const data = {
        ...getExerciceMonthlyPeriod(exercice, 'short'),
        exercice,
        lastExercice: exercice - 1,
    };
    return t('humanResources.payState.period', data);
};
